import { usePageContext } from '@internal/page/PageContext';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';
import slugify from 'slugify';
import { getSlugifyOptions } from '../routing/slugs/getSlugifyOptions';
import { localeNamespace } from './i18n/Locale';
import { TLocale } from './i18n/TLocale';
import { MachineType } from './MachineType';

export const useMachineTypeName = () => {
    const { t } = useTranslation(localeNamespace.machineTypeNames);
    const { locale } = usePageContext();

    const getMachineTypeName = useCallback(
        (machineType: MachineType, type: 'plural' | 'singular') => {
            const translation: { one: string; many: string } = t(machineType, {
                returnObjects: true,
            });
            const value =
                type === 'singular' ? translation.one : translation.many;
            return value;
        },
        [t],
    );

    const lowerCaseMachineTypeName = useCallback(
        (machineType: string, locale: TLocale) =>
            !['de'].includes(locale) ? machineType.toLowerCase() : machineType,
        [],
    );

    const getMachineTypeForMachineTypeSlug = useCallback(
        (machineTypeSlug: string) => {
            const machineTypes = Object.values(MachineType);

            const slugifyOptions = getSlugifyOptions(locale);
            for (let i = 0; i < machineTypes.length; ++i) {
                const machineType = machineTypes[i];
                const translatedMachineType = getMachineTypeName(
                    machineType,
                    'plural',
                );

                if (
                    machineTypeSlug ===
                    slugify(translatedMachineType, slugifyOptions)
                ) {
                    return machineType;
                }
            }
            return null;
        },
        [getMachineTypeName, locale],
    );

    return {
        getMachineTypeName,
        lowerCaseMachineTypeName,
        getMachineTypeForMachineTypeSlug,
    };
};
